import React from 'react';
import styled, { css } from 'styled-components';

type NoticeTone = 'info' | 'danger' | 'success' | 'neutral';
type NoticeDensity = 'regular' | 'compact';
type NoticeAlign = 'left' | 'center';

const tones = {
    info: {
        background: 'linear-gradient(135deg, #ffffff 0%, #fff8f6 100%)',
        border: '#ffd8d0',
        accent: '#c80707',
        accentSoft: 'rgba(200, 7, 7, 0.12)',
        title: '#262626',
        shadow: '0 14px 36px rgba(200, 7, 7, 0.08)',
    },
    danger: {
        background: 'linear-gradient(135deg, #fff5f5 0%, #ffeae6 100%)',
        border: '#ffccc7',
        accent: '#cf1322',
        accentSoft: 'rgba(207, 19, 34, 0.13)',
        title: '#a8071a',
        shadow: '0 16px 42px rgba(200, 7, 7, 0.16)',
    },
    success: {
        background: 'linear-gradient(135deg, #f6ffed 0%, #effbe6 100%)',
        border: '#b7eb8f',
        accent: '#389e0d',
        accentSoft: 'rgba(56, 158, 13, 0.13)',
        title: '#237804',
        shadow: '0 12px 30px rgba(56, 158, 13, 0.12)',
    },
    neutral: {
        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        border: '#e8e8e8',
        accent: '#8c8c8c',
        accentSoft: 'rgba(140, 140, 140, 0.14)',
        title: '#262626',
        shadow: '0 10px 28px rgba(0, 0, 0, 0.08)',
    },
};

const toneCss = (tone: NoticeTone) => {
    const values = tones[tone];
    return css`
        background: ${values.background};
        border-color: ${values.border};
        box-shadow: ${values.shadow};

        &::before {
            background: ${values.accent};
        }
    `;
};

const NoticeCard = styled.div<{
    $tone: NoticeTone;
    $density: NoticeDensity;
    $align: NoticeAlign;
}>`
    position: relative;
    overflow: hidden;
    border: 1px solid;
    border-radius: 14px;
    color: #595959;
    text-align: ${({ $align }) => $align};
    ${({ $tone }) => toneCss($tone)}
    padding: ${({ $density }) => ($density === 'compact' ? '13px 16px 13px 18px' : '24px 28px 22px 30px')};

    &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 4px;
    }
`;

const Header = styled.div<{ $align: NoticeAlign; $density: NoticeDensity }>`
    display: flex;
    align-items: ${({ $align }) => ($align === 'center' ? 'center' : 'flex-start')};
    justify-content: ${({ $align }) => ($align === 'center' ? 'center' : 'flex-start')};
    gap: ${({ $density }) => ($density === 'compact' ? '6px' : '10px')};

    ${({ $align }) =>
        $align === 'center' &&
        css`
            flex-direction: column;
        `}
`;

const Content = styled.div`
    min-width: 0;
`;

const Title = styled.h3<{ $tone: NoticeTone; $density: NoticeDensity }>`
    margin: 0;
    color: ${({ $tone }) => tones[$tone].title};
    font-size: ${({ $density }) => ($density === 'compact' ? '15px' : '20px')};
    line-height: 1.25;
    font-weight: 700;
`;

const Body = styled.div<{ $density: NoticeDensity }>`
    margin-top: ${({ $density }) => ($density === 'compact' ? '3px' : '10px')};
    font-size: ${({ $density }) => ($density === 'compact' ? '12px' : '15px')};
    line-height: ${({ $density }) => ($density === 'compact' ? '1.35' : '1.6')};
    font-weight: 400;

    a {
        color: #c80707;
        font-weight: 600;
        text-decoration: underline;
        text-underline-offset: 2px;

        &:hover {
            color: #a00606;
        }
    }

    strong {
        color: #262626;
        font-weight: 700;
    }
`;

const Action = styled.div<{ $density: NoticeDensity }>`
    margin-top: ${({ $density }) => ($density === 'compact' ? '6px' : '14px')};
    font-size: ${({ $density }) => ($density === 'compact' ? '12px' : '13px')};
    font-weight: 600;

    a {
        color: #c80707;
        text-decoration: underline;
        text-underline-offset: 2px;

        &:hover {
            color: #a00606;
        }
    }
`;

const Footer = styled.div<{ $density: NoticeDensity }>`
    margin-top: ${({ $density }) => ($density === 'compact' ? '5px' : '14px')};
    font-size: ${({ $density }) => ($density === 'compact' ? '11px' : '12px')};
    line-height: 1.45;
    color: #8c8c8c;
`;

interface CreditStatusNoticeProps {
    title: React.ReactNode;
    children?: React.ReactNode;
    action?: React.ReactNode;
    footer?: React.ReactNode;
    tone?: NoticeTone;
    density?: NoticeDensity;
    align?: NoticeAlign;
    className?: string;
    style?: React.CSSProperties;
}

export const CreditStatusNotice: React.FC<CreditStatusNoticeProps> = ({
    title,
    children,
    action,
    footer,
    tone = 'info',
    density = 'regular',
    align = 'left',
    className,
    style,
}) => (
    <NoticeCard className={className} style={style} $tone={tone} $density={density} $align={align}>
        <Header $align={align} $density={density}>
            <Content>
                <Title $tone={tone} $density={density}>
                    {title}
                </Title>
                {children && <Body $density={density}>{children}</Body>}
                {action && <Action $density={density}>{action}</Action>}
                {footer && <Footer $density={density}>{footer}</Footer>}
            </Content>
        </Header>
    </NoticeCard>
);
